import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Bookmark, Briefcase, MapPin } from 'lucide-react'
import {
  getMySavedJobs, unsaveJob, WORK_TYPE_LABELS, postedText,
  type ApiJobSaved, type ApiJobListItem,
} from '@/services/jobService'
import CompanyLogo from '@/components/ui/CompanyLogo'

function salaryText(job: Pick<ApiJobListItem, 'salary_min' | 'salary_max' | 'currency'>): string {
  const { salary_min: min, salary_max: max } = job
  if (!min && !max) return 'ไม่ระบุเงินเดือน'
  const fmt = (n: number) => n.toLocaleString('th-TH')
  const range = min && max ? `${fmt(min)} – ${fmt(max)}` : fmt((min || max) as number)
  return `฿${range} ${job.currency}/เดือน`
}

// หน้า "งานที่บันทึกไว้" (บุคคลทั่วไป) — รายการจาก GET /api/job-saved (ตาราง m_job_saved)
// กดไอคอนบุ๊กมาร์กเพื่อเอาออก — งานที่ปิดรับสมัครแล้วยังแสดง แต่ไม่มีลิงก์ไปดูประกาศ

export default function SavedJobsTab() {
  const [items, setItems] = useState<ApiJobSaved[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [actionError, setActionError] = useState('')

  useEffect(() => {
    getMySavedJobs()
      .then(setItems)
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'โหลดงานที่บันทึกไว้ไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [])

  const remove = async (jobId: number) => {
    setActionError('')
    const before = items
    setItems((prev) => prev.filter((s) => s.job_id !== jobId))   // ลบออกจากจอก่อน ถ้า API พลาดค่อยย้อนกลับ
    try {
      await unsaveJob(jobId)
    } catch (err) {
      setItems(before)
      setActionError(err instanceof Error ? err.message : 'เอางานออกจากรายการไม่สำเร็จ')
    }
  }

  if (loading) {
    return <div className="flex items-center justify-center rounded-2xl border border-dashed border-[#E1DCF5] bg-[#FAFAFF] py-20 text-sm text-[#69738A]">กำลังโหลด...</div>
  }
  if (loadError) {
    return <div className="rounded-2xl border border-dashed border-rose-200 bg-rose-50 px-6 py-10 text-center text-sm text-rose-600">{loadError}</div>
  }

  if (items.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-[#E1DCF5] bg-[#FAFAFF] px-6 py-16 text-center">
        <Bookmark className="mx-auto mb-3 h-8 w-8 text-[#9691AC]" />
        <p className="text-sm text-[#69738A]">ยังไม่มีงานที่บันทึกไว้</p>
        <Link to="/jobs" className="mt-3 inline-block text-sm font-medium text-accent hover:underline">ดูตำแหน่งงานที่เปิดรับ</Link>
      </div>
    )
  }

  return (
    <div>
      {actionError && <p className="mb-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-600">{actionError}</p>}
      <ul className="space-y-3">
        {items.map(({ saved_id, job_id, job }) => {
          const closed = job.status !== 'ACTIVE'
          return (
            <li key={saved_id} className="rounded-2xl border border-[#E7E5FF] bg-white p-4 sm:p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <CompanyLogo name={job.company_name || job.title} logoUrl={job.company_logo_url} size={44} className="rounded-full" initialsCount={1} />
                  <div>
                    <p className="text-[15px] font-semibold text-[#19172A]">{job.title}</p>
                    <p className="text-sm text-[#69738A]">{job.company_name || '-'}</p>
                    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#9691AC]">
                      <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {job.location}</span>
                      <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {WORK_TYPE_LABELS[job.work_type] || job.work_type}</span>
                      <span>{salaryText(job)}</span>
                      {job.published_at && <span>{postedText(job.published_at)}</span>}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => remove(job_id)}
                  title="เอาออกจากงานที่บันทึก"
                  className="shrink-0 rounded-md border border-accent/40 bg-accent/5 p-2 text-accent transition hover:bg-accent/10"
                >
                  <Bookmark className="h-4 w-4 fill-accent" />
                </button>
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-[#F0EEFB] pt-3">
                {closed
                  ? <span className="text-[11px] text-[#9691AC]">ตำแหน่งนี้ปิดรับสมัครแล้ว</span>
                  : <Link to={`/jobs?job=${job_id}`} className="text-xs font-medium text-accent hover:underline">ดูประกาศงาน</Link>}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
